import { useEffect, useState, useCallback } from 'react'
import { apiClient } from '@/lib/api-client'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { ChevronDown, ChevronLeft, ChevronRight, Search } from 'lucide-react'
import { useSearchParams } from 'react-router-dom'

interface AuditLogEntry {
  id: string
  userId: string
  action: string
  resource: string
  resourceId?: string
  detailsJson: string
  ipAddress?: string
  success: boolean
  error?: string
  createdAt: string
}

export default function AdminAuditLogsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [logs, setLogs] = useState<AuditLogEntry[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())

  const page = parseInt(searchParams.get('page') || '1')
  const pageSize = 50
  const userIdFilter = searchParams.get('userId') || ''

  const loadLogs = useCallback(async () => {
    setLoading(true)
    const offset = (page - 1) * pageSize
    const resp = await apiClient.listAuditLogs({
      userId: userIdFilter || undefined,
      limit: pageSize,
      offset,
    })
    if (resp.success && resp.data) {
      const data = resp.data as { auditLogs: AuditLogEntry[]; total: number }
      setLogs(data.auditLogs || [])
      setTotal(data.total || 0)
    }
    setLoading(false)
  }, [page, userIdFilter])

  useEffect(() => { loadLogs() }, [loadLogs])

  const updateFilter = (key: string, value: string) => {
    const params = new URLSearchParams(searchParams)
    if (value) {
      params.set(key, value)
    } else {
      params.delete(key)
    }
    params.set('page', '1')
    setSearchParams(params)
  }

  const toggleExpanded = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const totalPages = Math.ceil(total / pageSize)

  const formatDetails = (json: string) => {
    try {
      return JSON.stringify(JSON.parse(json), null, 2)
    } catch {
      return json
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Audit Logs</h1>
        <p className="text-sm text-muted-foreground">{total} entries</p>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Filter by user ID..."
            value={userIdFilter}
            onChange={(e) => updateFilter('userId', e.target.value)}
            className="pl-9 h-9"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
        </div>
      ) : (
        <div className="app-surface rounded-lg overflow-x-auto">
          <table className="w-full text-sm min-w-[600px]">
            <thead>
              <tr className="border-b text-left text-xs font-medium text-muted-foreground">
                <th className="w-8 px-2 py-3"></th>
                <th className="px-4 py-3">Timestamp</th>
                <th className="px-4 py-3">Action</th>
                <th className="px-4 py-3">Resource</th>
                <th className="px-4 py-3">Actor</th>
                <th className="px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((entry) => (
                <Collapsible key={entry.id} asChild open={expandedIds.has(entry.id)} onOpenChange={() => toggleExpanded(entry.id)}>
                  <>
                    <CollapsibleTrigger asChild>
                      <tr className="border-b cursor-pointer transition-colors hover:bg-muted/50">
                        <td className="px-2 py-3">
                          <ChevronDown className={`h-3.5 w-3.5 text-muted-foreground transition-transform ${expandedIds.has(entry.id) ? 'rotate-180' : ''}`} />
                        </td>
                        <td className="px-4 py-3 tabular-nums text-xs">
                          {new Date(entry.createdAt).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 font-mono text-xs">{entry.action}</td>
                        <td className="px-4 py-3">
                          <span className="text-xs">{entry.resource}</span>
                          {entry.resourceId && (
                            <span className="ml-1 font-mono text-[10px] text-muted-foreground">{entry.resourceId.slice(0, 8)}</span>
                          )}
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                          {entry.userId.slice(0, 8)}...
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant={entry.success ? 'default' : 'destructive'} className="text-[10px]">
                            {entry.success ? 'OK' : 'FAIL'}
                          </Badge>
                        </td>
                      </tr>
                    </CollapsibleTrigger>
                    <CollapsibleContent asChild>
                      <tr className="border-b bg-muted/30">
                        <td colSpan={6} className="px-6 py-3">
                          <div className="space-y-2">
                            {entry.ipAddress && (
                              <p className="text-xs text-muted-foreground">IP: {entry.ipAddress}</p>
                            )}
                            {entry.error && (
                              <p className="text-xs text-destructive">Error: {entry.error}</p>
                            )}
                            <pre className="max-h-40 overflow-auto rounded bg-background p-3 font-mono text-xs">
                              {formatDetails(entry.detailsJson)}
                            </pre>
                          </div>
                        </td>
                      </tr>
                    </CollapsibleContent>
                  </>
                </Collapsible>
              ))}
              {logs.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">No audit logs found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Page {page} of {totalPages}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => updateFilter('page', String(page - 1))}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => updateFilter('page', String(page + 1))}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
